import { breakpointSizes, Size } from "@brightagency/headstart-common";
import debounce from "lodash/debounce";
// import throttle from "lodash/throttle";
import * as qs from "query-string";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import createPersistedState from "use-persisted-state";
import { getListingDistanceFromUserString, useSuburbCoords, useSuburbName } from "..";
import { siteData } from "../../constants";
import { CoordType, SortModes, WPPost, RegionRedirect } from "../../constants/types";
import { AllPostTypesAcf, ListingAcf } from "../../constants/types/acf";
import { useBaseUrl, useConditions, usePopulations, useSortMode } from "../../services/app-state";
import { fetchJSON } from "../ajax";
import { createUniqueLocalStorageKey } from "../basic-only";

export const LinkInterceptor: FC = ({ children }) => {
    const contentRef = React.useRef<HTMLDivElement>(null);

    const history = useHistory();
    React.useEffect(() => {
        if (contentRef.current) {
            const content = contentRef.current;
            const handler = (event: MouseEvent) => {
                const target = event.target;
                if (target instanceof HTMLAnchorElement) {
                    if (target.href.includes(siteData.siteURL)) {
                        event.preventDefault();
                        const url = new URL(target.href);
                        let path = url.pathname;
                        if (siteData.installPath !== "/") {
                            path = url.pathname.replace(siteData.installPath, "");
                        }
                        history.push(path + url.search);
                    }
                }
            };
            content.addEventListener("click", handler);
            return () => {
                content.removeEventListener("click", handler);
            };
        }
        return () => {};
    }, [contentRef, history]);

    return <div ref={contentRef}>{children}</div>;
};

export const useKeyPress = (key: string, callback: (event: KeyboardEvent) => void) => {
    React.useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (event.key == key) {
                callback(event);
            }
        };
        window.addEventListener("keydown", handler);
        return () => {
            window.removeEventListener("keydown", handler);
        };
    }, [callback, key]);
};

export const useClickOutsideEl = (
    elRef: React.MutableRefObject<HTMLElement | null>,
    callback: (event: MouseEvent) => void,
) => {
    React.useEffect(() => {
        if (elRef && elRef.current) {
            const handler = (event: MouseEvent) => {
                if (
                    event.target instanceof HTMLElement &&
                    elRef &&
                    elRef.current &&
                    !elRef.current.contains(event.target)
                ) {
                    callback(event);
                }
            };
            setTimeout(() => {
                window.addEventListener("click", handler);
            }, 500);
            return () => {
                window.removeEventListener("click", handler);
            };
        }
    }, [callback, elRef]);
};

export const useNavigate = () => {
    const history = useHistory();
    return (path: string) => {
        // const withoutSearch = history.location.pathname;
        const withSearch = history.location.pathname + history.location.search;
        const withEmptyQuery = history.location.pathname + "?";

        const equalUrls = [withSearch, withEmptyQuery];

        if (!equalUrls.includes(path)) {
            history.push(path);
        }
    };
};

export const useHashUpdater = () => {
    const history = useHistory();

    const updateHash = useCallback(
        (obj: Record<string, string | number | undefined>) => {
            const currentHash = qs.parse(history.location.hash);
            const newHashString = qs.stringify({
                ...currentHash,
                ...obj,
            });
            history.push(history.location.pathname + history.location.search + "#" + newHashString);
        },
        [history],
    );

    return updateHash;
};

export const useWindowWidth = () => {
    const [width, setWidth] = React.useState(window.innerWidth);
    React.useEffect(() => {
        const handle = debounce(() => {
            setWidth(window.innerWidth);
        }, 250);
        window.addEventListener("resize", handle);
        return () => {
            window.removeEventListener("resize", handle);
        };
    }, []);
    return width;
};

// export const useWindowWidth = ({ debounceDelay = 250 } = {}) => {
//     const [width, setWidth] = React.useState(window.innerWidth);
//     React.useEffect(() => {
//         const handle = throttle(() => {
//             setWidth(window.innerWidth);
//         }, debounceDelay);
//         window.addEventListener("resize", handle);
//         return () => {
//             window.removeEventListener("resize", handle);
//         };
//     }, [debounceDelay]);
//     return width;
// };


const useUserCoordsSessionStorage = createPersistedState(
    createUniqueLocalStorageKey("user.coords"),
    window.sessionStorage,
);

export const useUserCoords = (active = true) => {
    const [coords, setCoords] = useUserCoordsSessionStorage<CoordType | undefined>();
    const [, { setSortMode, setLocationAccessDenied }] = useSortMode();

    React.useEffect(() => {
        if (active && (!coords || !coords.lat || !coords.lng)) {
            window.navigator.geolocation.getCurrentPosition(
                position => {
                    const newCoords: CoordType = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setCoords(newCoords);
                },
                error => {
                    if (error.code === error.PERMISSION_DENIED) {
                        setLocationAccessDenied();
                    }
                    setSortMode(SortModes.DistanceFromSuburb);
                },
            );
        }
    }, [active, coords, setCoords, setLocationAccessDenied, setSortMode]);

    return coords;
};

const useSavedItemsPersisted = createPersistedState(createUniqueLocalStorageKey("saved.items"));

export const useSavedItems = () => {
    const [items, setItems] = useSavedItemsPersisted<number[]>([]);

    const isItemSaved = (id: number) => items.includes(id);

    const saveItem = (id: number) => setItems(old => [...old, id]);

    const unsaveItem = (id: number) => setItems(old => old.filter(a => a !== id));

    const toggleSaved = (id: number) => (isItemSaved(id) ? unsaveItem(id) : saveItem(id));

    const clearAllItems = () => setItems([]);

    return {
        items,
        isItemSaved,
        saveItem,
        unsaveItem,
        toggleSaved,
        clearAllItems,
    };
};

type SavedItemsData = {
    listings: WPPost<ListingAcf>[];
    pages: WPPost<AllPostTypesAcf>[];
};

export const useSavedItemsData = () => {
    const { items } = useSavedItems();
    const baseUrl = useBaseUrl();

    const fetchSavedItemsData = useCallback((): Promise<SavedItemsData> => {
        if (items.length) {
            return fetchJSON<SavedItemsData>(
                `${baseUrl}/wp-json/headstart/v1/saved-items?ids=${items.join(",")}`,
            );
        } else {
            return Promise.resolve({ listings: [], pages: [] });
        }
    }, [baseUrl, items]);

    return useQuery(["saved-items-data", items], () => fetchSavedItemsData(), {
        keepPreviousData: true,
    });
};

export function useResource<T>(fetcher: () => Promise<T>) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [data, setData] = useState<T | undefined>();

    const fetchData = useCallback(async () => {
        if (!loading) {
            setLoading(true);
            try {
                const d = await fetcher();
                setData(d);
            } catch (e) {
                console.error(e);
                setError(e);
            } finally {
                setLoading(false);
            }
        }
    }, [loading, fetcher]);

    return { loading, error, data, fetchData };
}

export const useScrollYPosition = (threshold: number) => {
    const [scrolled, setScrolled] = React.useState(false);

    useEffect(() => {
        const handle = () => {
            if (window.scrollY > threshold) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        handle();
        window.addEventListener("scroll", handle);
        return () => {
            window.removeEventListener("scroll", handle);
        };
    }, [threshold]);

    return scrolled;
};

export const useFeedbackButtonVisibility = createPersistedState(
    createUniqueLocalStorageKey("feedback-button.dismissed"),
    sessionStorage,
);

export const usePopulationSlugTransformer = () => {
    const [populations] = usePopulations();

    const populationSlugToPost = useCallback(
        (slug: string) => populations.find(c => c.slug === slug),
        [populations],
    );

    const populationSlugToId = useCallback(
        (slug: string) => {
            return populationSlugToPost(slug)?.id;
        },
        [populationSlugToPost],
    );

    const populationSlugToTitle = useCallback(
        (slug: string) => {
            return populationSlugToPost(slug)?.title.rendered;
        },
        [populationSlugToPost],
    );

    return { populationSlugToId, populationSlugToPost, populationSlugToTitle };
};

export const useConditionSlugTransformer = () => {
    const [conditions] = useConditions();

    const conditionSlugToPost = useCallback(
        (slug: string) => conditions.find(c => c.slug === slug),
        [conditions],
    );

    const conditionSlugToId = useCallback(
        (slug: string) => {
            return conditionSlugToPost(slug)?.id;
        },
        [conditionSlugToPost],
    );

    const conditionSlugToTitle = useCallback(
        (slug: string) => {
            return conditionSlugToPost(slug)?.title.rendered;
        },
        [conditionSlugToPost],
    );

    return { conditionSlugToId, conditionSlugToPost, conditionSlugToTitle };
};

export const useSortingCoords = () => {
    return useSuburbCoords();
};

export const useDisplayCoords = () => {
    const [sortMode] = useSortMode();
    const suburbCoords = useSuburbCoords();
    const userCoords = useUserCoords(sortMode === SortModes.DistanceFromUser);
    if (sortMode === SortModes.DistanceFromSuburb) {
        return suburbCoords;
    }
    return userCoords ?? suburbCoords;
};

export const useListingDistance = (listing: WPPost<ListingAcf> | undefined) => {
    const hasLocations = Boolean((listing?.acf.locations || []).length);
    const [sortMode] = useSortMode();
    const suburbName = useSuburbName();
    const coords = useDisplayCoords();

    const distanceString = useMemo(() => {
        if (!listing || !coords) {
            return undefined;
        }
        return getListingDistanceFromUserString(listing, coords, sortMode, suburbName ?? "");
    }, [listing, coords, sortMode, suburbName]);

    if (!hasLocations || !listing) {
        return null;
    }

    return distanceString;
};


export const useBreakpoint = (breakpoint: Size) => {
    return useCustomBreakpointSize(breakpointSizes[breakpoint]);
};

export const useCustomBreakpointSize = (breakpoint: number) => {
    const width = useWindowWidth();
    return width >= breakpoint;
};

const useRememberedRegion_inner = createPersistedState(window.REGION_REMEMBER_LOCAL_STORAGE_KEY);
export const useRememberedRegion = () => {
    return useRememberedRegion_inner<RegionRedirect>();
};